<template>
  <div class="min-h-screen min-w-0 overflow-x-hidden bg-slate-50">
    <Navbar
      v-if="isAuthenticated"
      :current-user="currentUser"
      @signed-out="handleSignedOut"
    />
    <main :class="isAuthenticated ? 'min-h-screen min-w-0 overflow-x-hidden pt-16 lg:pl-64 lg:pt-20' : 'min-h-screen min-w-0 overflow-x-hidden'">
      <div :class="isAuthenticated ? 'mx-auto min-w-0 w-full max-w-[1680px] px-3 pb-10 pt-4 sm:px-5 sm:pt-5 lg:px-7 lg:pt-7' : ''">
        <router-view @login-success="handleLoginSuccess" />
      </div>
    </main>
  </div>
</template>

<script>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import Navbar from './Navbar.vue';
import api from '../services/api';

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch (_) {
    return {};
  }
};

export default {
  name: 'App',
  components: { Navbar },
  setup() {
    const router = useRouter();
    const storedUser = readStoredUser();
    const hasStoredUser = (user) => Boolean(user?.id || user?.username);
    const startsOnLogin = typeof window !== 'undefined' && window.location.pathname === '/login';
    const isAuthenticated = ref(!startsOnLogin && hasStoredUser(storedUser));
    const currentUser = ref(storedUser);
    let sessionSequence = 0;

    const refreshAuthState = async () => {
      const sequence = ++sessionSequence;
      currentUser.value = readStoredUser();

      try {
        const response = await api.getMe();
        if (sequence !== sessionSequence) return;
        const data = response.data || {};
        const baseUser = data.current_user || data.user || data;
        const user = (data.user || data.current_user) ? {
          ...baseUser,
          ai_wallet: data.ai_wallet || baseUser.ai_wallet,
          permissions: data.permissions || baseUser.permissions
        } : baseUser;
        if (user && typeof user === 'object') {
          isAuthenticated.value = true;
          currentUser.value = user;
          localStorage.setItem('user', JSON.stringify(user));
        }
      } catch (error) {
        if (sequence !== sessionSequence) return;
        // 401 由 API 拦截器统一清理并跳转。其余错误保留登录返回的本地用户，
        // 避免一次临时网络错误让整个管理壳层消失。
        if (error?.response?.status === 401) {
          isAuthenticated.value = false;
          currentUser.value = {};
          localStorage.removeItem('user');
          if (router.currentRoute.value.name !== 'Login') {
            router.replace({ name: 'Login' });
          }
        } else {
          const fallbackUser = readStoredUser();
          if (router.currentRoute.value.name !== 'Login' && hasStoredUser(fallbackUser)) {
            isAuthenticated.value = true;
            currentUser.value = fallbackUser;
          }
          console.error('Unable to refresh current user:', error);
        }
      }
    };

    const handleLoginSuccess = (user) => {
      sessionSequence += 1;
      const authenticatedUser = user && typeof user === 'object' ? user : readStoredUser();
      isAuthenticated.value = hasStoredUser(authenticatedUser);
      currentUser.value = authenticatedUser;
      if (isAuthenticated.value) {
        localStorage.setItem('user', JSON.stringify(authenticatedUser));
      }
    };

    const handleStorage = (event) => {
      if (!event || event.key === 'user') refreshAuthState();
    };

    const handleSignedOut = () => {
      sessionSequence += 1;
      isAuthenticated.value = false;
      currentUser.value = {};
    };

    const handleInvalidSession = () => {
      sessionSequence += 1;
      isAuthenticated.value = false;
      currentUser.value = {};
      localStorage.removeItem('user');
    };

    onMounted(() => {
      refreshAuthState();
      window.addEventListener('auth-session-invalid', handleInvalidSession);
      window.addEventListener('storage', handleStorage);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('auth-session-invalid', handleInvalidSession);
      window.removeEventListener('storage', handleStorage);
    });

    return {
      isAuthenticated,
      currentUser,
      refreshAuthState,
      handleLoginSuccess,
      handleSignedOut
    };
  }
};
</script>

<style>
html,
body,
#app {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

body {
  overflow-x: hidden;
}
</style>
